import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import {
  WebhookIntegrationRecord,
  WebhookIntegrationStatus,
} from "@/shared/domain/integrations";
import { useWebhookIntegration } from "@/shared/domain/integrations/hooks/useWebhookIntegration";

import { ConfigureWebhookContent } from "./ConfigureWebhookContent";
import styles from "./FoxgloveIntegrationStepper.module.css";
import { IntegrationCompleteContent } from "./IntegrationCompleteContent";
import { ProvisionApiKeyContent } from "./ProvisionApiKeyContent";
import { ProvisionWebhookContent } from "./ProvisionWebhookContent";

enum FoxgloveIntegrationPhase {
  NotStarted = 0,
  WebhookProvisioned = 1,
  WebhookConfigured = 2,
  APIKeyProvisioned = 3,
  Complete = 4,
}

export interface FoxgloveIntegrationStepperProps {
  webhookEndpointId?: string;
}

const integrationRecordToPhase = (
  integration: WebhookIntegrationRecord | null | undefined,
): FoxgloveIntegrationPhase => {
  if (!integration) {
    return FoxgloveIntegrationPhase.NotStarted;
  } else if (integration.status === WebhookIntegrationStatus.Active) {
    return FoxgloveIntegrationPhase.Complete;
  } else if (!integration.secret_registered) {
    return FoxgloveIntegrationPhase.WebhookProvisioned;
  } else if (!integration.api_key_registered) {
    return FoxgloveIntegrationPhase.WebhookConfigured;
  } else {
    return FoxgloveIntegrationPhase.APIKeyProvisioned;
  }
};

interface FoxgloveStepContentProps {
  phase: FoxgloveIntegrationPhase;
  foxgloveIntegration: WebhookIntegrationRecord | null | undefined;
}

function FoxgloveStepContent({
  phase,
  foxgloveIntegration,
}: FoxgloveStepContentProps) {
  switch (phase) {
    case FoxgloveIntegrationPhase.NotStarted:
      return <ProvisionWebhookContent />;
    case FoxgloveIntegrationPhase.WebhookProvisioned:
      return (
        <ConfigureWebhookContent foxgloveIntegration={foxgloveIntegration} />
      );
    case FoxgloveIntegrationPhase.WebhookConfigured:
      return (
        <ProvisionApiKeyContent foxgloveIntegration={foxgloveIntegration} />
      );
    case FoxgloveIntegrationPhase.APIKeyProvisioned:
    case FoxgloveIntegrationPhase.Complete:
      return (
        <IntegrationCompleteContent foxgloveIntegration={foxgloveIntegration} />
      );
    default:
      return <ProvisionWebhookContent />;
  }
}

export function FoxgloveIntegrationStepper({
  webhookEndpointId,
}: FoxgloveIntegrationStepperProps) {
  const theme = useTheme();
  const useTwoColumns = useMediaQuery(theme.breakpoints.up("md"));

  const { data: foxgloveIntegration } =
    useWebhookIntegration(webhookEndpointId);

  const phase = integrationRecordToPhase(foxgloveIntegration);

  const steps = [
    {
      label: "Provision Webhook URL",
      component: <ProvisionWebhookContent />,
    },
    {
      label: "Configure Webhook in Foxglove",
      component: (
        <ConfigureWebhookContent foxgloveIntegration={foxgloveIntegration} />
      ),
    },
    {
      label: "Provision Foxglove API Key",
      component: (
        <ProvisionApiKeyContent foxgloveIntegration={foxgloveIntegration} />
      ),
    },
    {
      label: "Integration Complete",
      component: (
        <IntegrationCompleteContent foxgloveIntegration={foxgloveIntegration} />
      ),
    },
  ];

  const phaseIdx = phase.valueOf() === steps.length - 1 ? phase + 1 : phase;

  // Split layout for large screens
  if (useTwoColumns) {
    return (
      <Box className={styles.splitLayoutContainer}>
        <Box
          className={styles.splitLayoutSidebar}
          sx={{
            borderRight: `1px solid ${theme.border.color}`,
          }}
        >
          <Stepper activeStep={phaseIdx} orientation="vertical">
            {steps.map((step) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        <Box className={styles.splitLayoutContent}>
          <Typography variant="h6" className={styles.marginBottom2}>
            {steps[phase]?.label ?? steps[0].label}
          </Typography>
          <FoxgloveStepContent
            phase={phase}
            foxgloveIntegration={foxgloveIntegration}
          />
        </Box>
      </Box>
    );
  }

  // Vertical layout for smaller screens
  return (
    <Box className={styles.verticalLayoutContainer}>
      <Stepper activeStep={phaseIdx} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel>{step.label}</StepLabel>
            <StepContent>{steps[index].component}</StepContent>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}
